/**
 * AuthService - Central authentication service with singleton pattern
 * Handles all token operations, authentication state, and event-driven updates
 */

import {
  LoginRequest,
  LoginResponse,
  AuthResult,
  RefreshTokenResponse,
  User,
  AuthEvent,
  AuthEventListener,
  AuthEventType,
  AuthConfig
} from '../types/auth';
import { SecureStorage } from './secureStorage';
import { logger } from '../utils/logger';

// Cookie-only auth mode (Phase 2). When true, the JWT lives ONLY in httpOnly
// cookies (never in JS/localStorage): no Bearer header, session is discovered
// via GET /auth/me, refresh/logout ride the cookie. Flag-gated so the default
// build keeps the dual behaviour (tokens in localStorage + Bearer) — flip
// VITE_COOKIE_AUTH=true to verify cookie-only end-to-end before enabling it.
const COOKIE_AUTH = import.meta.env.VITE_COOKIE_AUTH === 'true';

// Default configuration
const DEFAULT_CONFIG: AuthConfig = {
  apiBaseUrl: import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1',
  tokenRefreshThreshold: 300, // 5 minutes before expiration
  maxRetries: 3,
  retryDelay: 1000, // 1 second
};

/**
 * AuthService - Singleton class for managing authentication
 */
class AuthService {
  private static instance: AuthService;
  private config: AuthConfig;
  private eventListeners: Map<AuthEventType, Set<AuthEventListener>> = new Map();
  private refreshPromise: Promise<boolean> | null = null;
  private refreshTimeoutId: number | null = null;
  // In-memory session state for COOKIE_AUTH mode (no tokens in JS).
  private cookieUser: User | null = null;
  private cookieAuthenticated = false;
  private sessionBootstrapped = false;
  private lastCookieRefreshAt = 0;
  // Tracked access-token expiry (epoch ms) so we can refresh at REQUEST time,
  // independent of any setTimeout (which browsers throttle in background tabs).
  private cookieAccessExpiresAt = 0;
  // Default lifetime assumed when the real one is unknown (e.g. right after
  // bootstrap, where /auth/me doesn't return expires_in). The first refresh
  // corrects it from the response's expires_in.
  private static readonly COOKIE_DEFAULT_REFRESH_SEC = 1500;
  // Refresh this many seconds before expiry (request-time and timer).
  private static readonly COOKIE_REFRESH_MARGIN_SEC = 120;

  private constructor(config: Partial<AuthConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.initializeEventListeners();
    this.scheduleProactiveRefresh();
    this.registerWakeListeners();
  }

  /**
   * Listen for the tab becoming visible or regaining focus, and refresh the
   * access token if it's stale. Browsers throttle or suspend `setTimeout` in
   * backgrounded tabs, so the proactive-refresh timer can miss its slot and
   * the first burst of requests after the user returns each get a 401. The
   * `refreshPromise` guard inside `refreshToken` dedupes parallel callers.
   */
  private registerWakeListeners(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleFocus);
  }

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      void this.refreshIfStale();
    }
  };

  private handleFocus = (): void => {
    void this.refreshIfStale();
  };

  /**
   * Refresh the access token when it's inside the refresh threshold (or
   * already expired). No-op when the user isn't signed in.
   */
  private async refreshIfStale(): Promise<void> {
    if (COOKIE_AUTH) {
      // We can't read token expiry from JS, so refresh on wake (tab
      // visible/focused) if authenticated — throttled so rapid focus/blur
      // doesn't spam. This keeps the cookie (and the WS session that depends on
      // it) alive after the tab was idle past the access-token lifetime,
      // instead of relying only on the reactive 401→refresh of the next call.
      if (!this.cookieAuthenticated) return;
      const now = Date.now();
      if (now - this.lastCookieRefreshAt < 5 * 60 * 1000) return;
      this.lastCookieRefreshAt = now;
      await this.refreshToken();
      return;
    }
    if (!this.getRefreshToken()) return;
    if (!SecureStorage.isTokenExpiring(this.config.tokenRefreshThreshold)) return;
    await this.refreshToken();
  }

  /** Whether the app is running in cookie-only auth mode. */
  isCookieAuth(): boolean {
    return COOKIE_AUTH;
  }

  /** In cookie mode, false until the /auth/me bootstrap resolves. Always true
   * in dual mode (session is known synchronously from localStorage). */
  isSessionBootstrapped(): boolean {
    return !COOKIE_AUTH || this.sessionBootstrapped;
  }

  /**
   * Cookie mode: discover the session by calling /auth/me with the cookie.
   * Dual mode: no-op (session is read synchronously from storage).
   * Safe to call once at app start.
   */
  async bootstrapSession(): Promise<void> {
    if (!COOKIE_AUTH) {
      this.sessionBootstrapped = true;
      return;
    }
    // Wipe any tokens left in localStorage by a pre-flip (dual-mode) session —
    // cookie mode never uses them, but they'd sit there (XSS-readable) until a
    // manual logout. Clearing on every cookie-mode load closes that residue.
    SecureStorage.clearTokens();
    try {
      const fetchMe = () => fetch(`${this.config.apiBaseUrl}/auth/me`, { credentials: 'include' });
      let resp = await fetchMe();

      // The access-token cookie (short-lived) may have expired while the
      // refresh-token cookie (long-lived) is still valid. A raw /auth/me here
      // bypasses apiClient's reactive 401→refresh, so do it explicitly:
      // refresh via the cookie, then retry once. Without this, an expired
      // access token bounces the user to /login despite a valid session.
      let refreshedDuringBootstrap = false;
      if (resp.status === 401) {
        refreshedDuringBootstrap = await this.refreshToken();
        if (refreshedDuringBootstrap) {
          resp = await fetchMe();
        }
      }

      if (resp.ok) {
        this.cookieUser = await resp.json();
        this.cookieAuthenticated = true;
        // A bootstrap refresh already scheduled the next one; otherwise the
        // access token is valid but of unknown remaining lifetime, so use the
        // conservative default (the first proactive refresh corrects it).
        if (!refreshedDuringBootstrap) {
          this.scheduleCookieRefresh();
        }
      } else {
        this.cookieUser = null;
        this.cookieAuthenticated = false;
      }
    } catch {
      this.cookieUser = null;
      this.cookieAuthenticated = false;
    } finally {
      this.sessionBootstrapped = true;
      this.emitEvent({
        type: 'authStateChanged',
        timestamp: Date.now(),
        payload: { isAuthenticated: this.cookieAuthenticated, user: this.cookieUser || undefined },
      });
    }
  }

  /**
   * Get the singleton instance
   * @param config Optional configuration overrides
   * @returns AuthService instance
   */
  static getInstance(config?: Partial<AuthConfig>): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService(config);
    }
    return AuthService.instance;
  }

  /**
   * Initialize event listener collections
   */
  private initializeEventListeners(): void {
    const eventTypes: AuthEventType[] = [
      'authStateChanged',
      'tokensRefreshed',
      'authError',
      'tokenExpiring',
      'userUpdated'
    ];

    eventTypes.forEach(type => {
      this.eventListeners.set(type, new Set());
    });
  }

  /**
   * Add event listener for authentication events
   * @param eventType The event type to listen for
   * @param listener The callback function
   */
  addEventListener(eventType: AuthEventType, listener: AuthEventListener): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      listeners.add(listener);
    }
  }

  /**
   * Remove event listener
   * @param eventType The event type
   * @param listener The callback function to remove
   */
  removeEventListener(eventType: AuthEventType, listener: AuthEventListener): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      listeners.delete(listener);
    }
  }

  /**
   * Emit an authentication event
   * @param event The event to emit
   */
  private emitEvent(event: AuthEvent): void {
    const listeners = this.eventListeners.get(event.type);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(event);
        } catch (error) {
          logger.error(`Error in auth event listener for ${event.type}`, error);
        }
      });
    }
  }

  /**
   * Login with email and password
   * @param credentials Login credentials
   * @returns Authentication result
   */
  async login(credentials: LoginRequest): Promise<AuthResult> {
    try {
      const response = await fetch(`${this.config.apiBaseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
        // Store the httpOnly auth cookies the backend sets on login (required
        // cross-origin, e.g. dev front:3000 → api:8000).
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Login failed: ${response.statusText}`);
      }

      const loginResponse: LoginResponse = await response.json();

      if (COOKIE_AUTH) {
        // Tokens are set as httpOnly cookies by the backend; keep only the
        // user in memory. No token touches JS/localStorage.
        this.cookieUser = loginResponse.user;
        this.cookieAuthenticated = true;
        this.sessionBootstrapped = true;
        this.lastCookieRefreshAt = Date.now();
        this.scheduleCookieRefresh(loginResponse.expires_in);
      } else {
        // Store tokens securely
        const expiresAt = Math.floor(Date.now() / 1000) + loginResponse.expires_in;
        SecureStorage.setTokens(
          loginResponse.access_token,
          loginResponse.refresh_token,
          expiresAt
        );

        // Store user info
        SecureStorage.setUser(loginResponse.user);

        // Schedule proactive refresh
        this.scheduleProactiveRefresh();
      }

      // Emit authentication state change
      this.emitEvent({
        type: 'authStateChanged',
        timestamp: Date.now(),
        payload: {
          isAuthenticated: true,
          user: loginResponse.user,
        }
      });

      return {
        success: true,
        user: loginResponse.user,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      
      this.emitEvent({
        type: 'authError',
        timestamp: Date.now(),
        payload: {
          error: errorMessage,
          context: 'login',
        }
      });

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Logout and clear all authentication data
   */
  async logout(): Promise<void> {
    try {
      // Always call logout so the backend clears the cookies — in cookie mode
      // there is no JS token to gate on. Send the Bearer too if we have one.
      const accessToken = this.getAccessToken();
      try {
        await fetch(`${this.config.apiBaseUrl}/auth/logout`, {
          method: 'POST',
          headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {},
          // Send the cookies so the backend can clear them (Set-Cookie delete).
          credentials: 'include',
        });
      } catch (error) {
        logger.warn('Logout API call failed', error);
      }
    } catch (error) {
      logger.warn('Error during logout API call', error);
    } finally {
      // Always clear local data regardless of API call success
      this.clearAuthData();
      
      this.emitEvent({
        type: 'authStateChanged',
        timestamp: Date.now(),
        payload: {
          isAuthenticated: false,
        }
      });
    }
  }

  /**
   * Clear all authentication data
   */
  private clearAuthData(): void {
    SecureStorage.clearTokens();
    this.cookieUser = null;
    this.cookieAuthenticated = false;
    this.cookieAccessExpiresAt = 0;

    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
    
    this.refreshPromise = null;
  }

  /**
   * Get the current access token
   * @returns Access token or null if not authenticated
   */
  getAccessToken(): string | null {
    return SecureStorage.getAccessToken();
  }

  /**
   * Get the current refresh token
   * @returns Refresh token or null if not available
   */
  getRefreshToken(): string | null {
    return SecureStorage.getRefreshToken();
  }

  /**
   * Check if user is authenticated
   * @returns True if authenticated with valid tokens
   */
  isAuthenticated(): boolean {
    if (COOKIE_AUTH) return this.cookieAuthenticated;
    return SecureStorage.hasValidTokens() && !SecureStorage.isTokenExpiring(0);
  }

  /**
   * Get current user information
   * @returns User object or null if not authenticated
   */
  getCurrentUser(): User | null {
    if (COOKIE_AUTH) return this.cookieUser;
    return SecureStorage.getUser();
  }

  /**
   * Refresh access token atomically
   * @returns True if refresh was successful
   */
  async refreshToken(): Promise<boolean> {
    // If refresh is already in progress, return the existing promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual token refresh
   * @returns True if refresh was successful
   */
  private async performTokenRefresh(): Promise<boolean> {
    if (COOKIE_AUTH) {
      // Cookie mode: the refresh token is an httpOnly cookie. POST with an
      // empty body; the backend reads the cookie and rotates both cookies.
      try {
        const response = await fetch(`${this.config.apiBaseUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error(`Token refresh failed: ${response.statusText}`);
        }
        // New tokens are delivered as cookies; nothing to store in JS. The body
        // still carries expires_in — use it to schedule the next proactive
        // refresh precisely.
        const body = await response.json().catch(() => ({}));
        this.lastCookieRefreshAt = Date.now();
        this.scheduleCookieRefresh(body?.expires_in);
        this.emitEvent({
          type: 'tokensRefreshed',
          timestamp: Date.now(),
          payload: { accessToken: '', refreshToken: '', expiresAt: 0 },
        });
        return true;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Token refresh failed';
        this.cookieAuthenticated = false;
        this.emitEvent({
          type: 'authError',
          timestamp: Date.now(),
          payload: { error: errorMessage, code: 'TOKEN_REFRESH_FAILED', context: 'tokenRefresh' },
        });
        return false;
      }
    }

    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.emitEvent({
        type: 'authError',
        timestamp: Date.now(),
        payload: {
          error: 'No refresh token available',
          code: 'NO_REFRESH_TOKEN',
        }
      });
      
      this.clearAuthData();
      return false;
    }
    
    // Validate refresh token format
    if (refreshToken.length < 10 || !refreshToken.includes('.')) {
      this.emitEvent({
        type: 'authError',
        timestamp: Date.now(),
        payload: {
          error: 'Invalid refresh token format',
          code: 'INVALID_REFRESH_TOKEN_FORMAT',
        }
      });
      
      this.clearAuthData();
      return false;
    }

    try {
      // Add additional validation before making request
      if (!refreshToken || refreshToken.length < 10) {
        throw new Error('Invalid refresh token: token is too short or missing');
      }
      
      const response = await fetch(`${this.config.apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
        // Send/rotate the httpOnly refresh cookie alongside the body token.
        credentials: 'include',
      });

      if (!response.ok) {
        try {
          const errorData = await response.json();
          throw new Error(errorData.message || `Token refresh failed: ${response.statusText}`);
        } catch (e) {
          throw new Error(`Token refresh failed: ${response.statusText}`);
        }
      }

      const refreshResponse: RefreshTokenResponse = await response.json();
      
      // Validate response before storing
      if (!refreshResponse.access_token || !refreshResponse.refresh_token) {
        throw new Error('Invalid refresh response: missing tokens');
      }
      
      if (refreshResponse.access_token.length < 10 || refreshResponse.refresh_token.length < 10) {
        throw new Error('Invalid refresh response: tokens too short');
      }
      
      // Store new tokens
      const expiresAt = Math.floor(Date.now() / 1000) + (refreshResponse.expires_in || 3600);
      SecureStorage.setTokens(
        refreshResponse.access_token,
        refreshResponse.refresh_token,
        expiresAt
      );

      // Schedule next proactive refresh
      this.scheduleProactiveRefresh();

      // Emit tokens refreshed event
      this.emitEvent({
        type: 'tokensRefreshed',
        timestamp: Date.now(),
        payload: {
          accessToken: refreshResponse.access_token,
          refreshToken: refreshResponse.refresh_token,
          expiresAt,
        }
      });

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Token refresh failed';
      
      this.emitEvent({
        type: 'authError',
        timestamp: Date.now(),
        payload: {
          error: errorMessage,
          code: 'TOKEN_REFRESH_FAILED',
          context: 'tokenRefresh',
        }
      });

      // Clear auth data on refresh failure
      this.clearAuthData();
      
      return false;
    }
  }

  /**
   * Cookie mode: schedule a proactive refresh ~2 min before the access token
   * expires, so an unattended (but focused) tab never hits a burst of 401s when
   * the token lapses. Reschedules itself from each refresh's expires_in.
   */
  private scheduleCookieRefresh(expiresInSec?: number): void {
    const lifetime = expiresInSec && expiresInSec > 0
      ? expiresInSec
      : AuthService.COOKIE_DEFAULT_REFRESH_SEC;
    // Track expiry for the request-time gate (ensureFreshToken), the robust
    // path — timers alone are unreliable in throttled background tabs.
    this.cookieAccessExpiresAt = Date.now() + lifetime * 1000;

    // Timer is a best-effort secondary path (fires while the tab is active).
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
    const delayMs = Math.max(30, lifetime - AuthService.COOKIE_REFRESH_MARGIN_SEC) * 1000;
    this.refreshTimeoutId = window.setTimeout(() => {
      if (this.cookieAuthenticated) void this.refreshToken();
    }, delayMs);
  }

  /**
   * Cookie mode: if the access token is at/near expiry, refresh BEFORE the
   * caller fires its request — so a burst of parallel requests all await one
   * (deduped) refresh and go out with a fresh cookie, instead of each racing
   * out with the expired cookie and 401ing. Checks tracked expiry, so it works
   * even when background-tab timer throttling delayed the proactive refresh.
   * Never throws — on refresh failure the request proceeds and 401s naturally
   * (the reactive path then handles it).
   */
  async ensureFreshToken(): Promise<void> {
    if (!COOKIE_AUTH || !this.cookieAuthenticated) return;
    if (!this.cookieAccessExpiresAt) return;
    const marginMs = AuthService.COOKIE_REFRESH_MARGIN_SEC * 1000;
    if (Date.now() < this.cookieAccessExpiresAt - marginMs) return;
    try {
      await this.refreshToken();
    } catch {
      /* let the request proceed; reactive 401 path is the fallback */
    }
  }

  /**
   * Schedule proactive token refresh before expiration
   */
  private scheduleProactiveRefresh(): void {
    // Cookie mode has no token expiry in JS; renewal is reactive (401→refresh).
    if (COOKIE_AUTH) return;

    // Clear any existing timeout
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
    }

    const expiration = SecureStorage.getTokenExpiration();
    if (!expiration) {
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiration = expiration - now;
    
    // Schedule refresh when token is within the threshold
    const refreshDelay = Math.max(0, (timeUntilExpiration - this.config.tokenRefreshThreshold) * 1000);
    
    this.refreshTimeoutId = window.setTimeout(() => {
      this.emitEvent({
        type: 'tokenExpiring',
        timestamp: Date.now(),
        payload: {
          expiresIn: timeUntilExpiration,
        }
      });
      
      this.refreshToken();
    }, refreshDelay);
  }

  /**
   * Update user information
   * @param user Updated user object
   */
  updateUser(user: User): void {
    SecureStorage.setUser(user);
    
    this.emitEvent({
      type: 'userUpdated',
      timestamp: Date.now(),
      payload: {
        user,
      }
    });
  }

  /**
   * Get authentication headers for API requests
   * @returns Headers object with Authorization
   */
  getAuthHeaders(): Record<string, string> {
    // Cookie mode: no Bearer header — the httpOnly cookie carries auth.
    if (COOKIE_AUTH) return {};

    const token = this.getAccessToken();

    const headers: Record<string, string> = token ? { 'Authorization': `Bearer ${token}` } : {};

    return headers;
  }
}

// Export singleton instance
export const authService = AuthService.getInstance();

// Export class for testing purposes
export { AuthService };

export default authService;